import { BsXCircle } from '@react-icons/all-files/bs/BsXCircle';
import { computed } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { Input } from 'reactstrap';
import { bind } from '../Utils/Bind';
import {
    addDays,
    addHours,
    addMinutes,
    addSeconds,
    Duration,
    getDays,
    getHours,
    getMinutes,
    getSeconds,
    getTotalDays,
    getTotalHours,
    getTotalMinutes,
    getTotalSeconds,
} from '../Utils/Duration';
import { componentAutoReaction } from '../Utils/MobxTools';
import { IFormField } from '../Utils/PrimitiveFormField';

export interface ITimeSpanPickerProps {
    readonly className?: string;
    readonly disabled?: boolean;
    readonly required?: boolean;
    readonly field: IFormField<Duration | null | undefined>;
    readonly validation?: string;
}

@observer
export class TimeSpanPicker extends React.Component<ITimeSpanPickerProps> {
    public constructor(props: ITimeSpanPickerProps) {
        super(props);

        componentAutoReaction(
            this,
            () => this.validations,
            v => props.field.onMount(v)
        );
    }

    public render() {
        const style: React.CSSProperties = {
            display: 'inline',
            width: 68,
        };
        return (
            <span>
                <Input
                    name="days"
                    type="number"
                    style={style}
                    min={0}
                    placeholder="DD"
                    step={1}
                    value={this.days ?? ''}
                    onChange={this.onChange}
                    disabled={this.props.disabled}
                />
                <span> Days </span>
                <Input
                    name="hours"
                    type="number"
                    style={style}
                    min={0}
                    max={24}
                    placeholder="HH"
                    value={this.hours ?? ''}
                    onChange={this.onChange}
                    disabled={this.props.disabled}
                />
                <span> Hours </span>
                <Input
                    name="mins"
                    type="number"
                    style={style}
                    min={0}
                    max={60}
                    placeholder="MM"
                    value={this.mins ?? ''}
                    onChange={this.onChange}
                    disabled={this.props.disabled}
                />
                <span> Mins </span>
                <Input
                    name="secs"
                    type="number"
                    style={style}
                    min={0}
                    max={60}
                    placeholder="SS"
                    value={this.secs ?? ''}
                    onChange={this.onChange}
                    disabled={this.props.disabled}
                />
                <span> Secs </span>
                <button onClick={this.clear} disabled={this.props.disabled} className="btn btn-link">
                    <BsXCircle color="red" />
                </button>
                {this.props.field.submitted &&
                    this.props.field.errors.map((e, idx) => (
                        <span className="form-control-feedback" key={idx}>
                            {e}
                        </span>
                    ))}
            </span>
        );
    }

    @computed
    private get days() {
        if (this.props.field.value == undefined || this.props.field.value == null) return;
        return getDays(this.props.field.value);
    }

    @computed
    private get hours() {
        if (this.props.field.value == undefined || this.props.field.value == null) return;
        return getHours(this.props.field.value);
    }

    @computed
    private get mins() {
        if (this.props.field.value == undefined || this.props.field.value == null) return;
        return getMinutes(this.props.field.value);
    }

    @computed
    private get secs() {
        if (this.props.field.value == undefined || this.props.field.value == null) return;
        return getSeconds(this.props.field.value);
    }

    @bind
    private onChange(event: React.FormEvent<HTMLInputElement>) {
        const target = event.target as HTMLInputElement;
        const values = {
            days: this.days,
            hours: this.hours,
            mins: this.mins,
            secs: this.secs,
        };
        (values as any)[target.name] = target.valueAsNumber || 0;
        if (values.days != null || values.hours != null || values.mins != null || values.secs != null) {
            let value = new Duration(0);
            if (values.days != null) value = addDays(value, values.days);
            if (values.hours != null) value = addHours(value, values.hours);
            if (values.mins != null) value = addMinutes(value, values.mins);
            if (values.secs != null) value = addSeconds(value, values.secs);

            this.props.field.onChange(value);
        } else {
            this.props.field.onChange(undefined);
        }
    }

    @bind
    private clear(event: React.MouseEvent<HTMLButtonElement>) {
        event.preventDefault();

        this.props.field.onChange(undefined);
    }

    @computed
    private get validations() {
        const rules = new Set<string>(this.props.validation?.split('|'));
        if (this.props.required) rules.add('required');

        return Array.from(rules).join('|');
    }
}
